import React, { useEffect, useState, useRef } from "react";
import { findInitialSchoolYear } from "../../../util/schoolYear";
import DropdownsForAttendanceStatistics from "./components/DropdownsForAttendanceStatistics";
import useOrganizationSetting from "../../../hooks/useOrganizationSetting";
import { useLocation } from "react-router-dom";
import useRegistrationInfo from "../../../hooks/useRegistrationInfo";
import { generateRegistrationRows } from "../../../util/dealingArrObj";
import ChangeMonth from "./components/ChangeMonth";
import AttendanceStatisticsTable from "./components/AttendanceStatisticsTable";
import useAttendance from "../../../hooks/useAttendance";
import useSchoolYearPeriod from "../../../hooks/useSchoolYearPeriod";
import { useUIContext } from "../../../context/UIContext";
import LoadingInside from "../../../unicomponents/LoadingInside";
import SimpleAlertMessageBlock from "../../../unicomponents/SimpleAlertMessageBlock";
import { useAuthContext } from "../../../context/AuthContext";
import { useTranslation } from "react-i18next";
import * as XLSX from "xlsx";
import { useReactToPrint } from "react-to-print";
import useFlexibleData from "../../../hooks/useFlexibleData";

export default function AttendanceStatistics() {
  const { t } = useTranslation();
  const location = useLocation();
  const paths = location.pathname.split("/");
  // 배열의 두 번째 요소를 schoolId로 사용합니다.
  const schoolId = paths[1];
  //아래 조건으로 데이터를 불러오도록 합니다.
  const conditions = {
    schoolid: schoolId,
  };

  const {
    settingQuery: { data: settingData },
  } = useOrganizationSetting(conditions);

  const {
    registrationInfoQuery: { isLoading, data: registrationInfoData },
  } = useRegistrationInfo(conditions);

  const { attendanceQuery } = useAttendance(conditions);

  const {
    schoolYearPeriodQuery: { data: schoolYearPeriodData },
  } = useSchoolYearPeriod(conditions);

  const {
    flexibleDataQuery: { data: flexibleData },
  } = useFlexibleData({ ...conditions, main_category: "schoolSetting" });

  const additionalAttendance =
    flexibleData &&
    flexibleData.length > 0 &&
    flexibleData[0].data_value?.additionalAttendance;

  const { setAlertModalState } = useUIContext();

  const { hasPermission } = useAuthContext();

  //필터를 위한 모음
  const [organSettingInfo, setOrganSettingInfo] = useState({
    schoolYear: null,
    schoolYearId: "",
    hierarchyOneId: "",
    hierarchyTwoId: "",
    hierarchyThreeId: "",
    selectedStudent: "",
    organPathForSort: "/",
  });

  const [rows, setRows] = useState([]);
  const [attendanceRecord, setAttendanceRecord] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [thisYearDates, setThisYearDates] = useState(null);
  const [validYearMonths, setValidYearMonths] = useState([]);

  // selectedMonth를 "YYYY-MM" 형식으로 변경 (예: "2024-02")
  const [selectedMonth, setSelectedMonth] = useState("");
  const [attendanceMode, setAttendanceMode] = useState("default");

  const printAreaRef = useRef(null);

  useEffect(() => {
    if (!additionalAttendance && attendanceMode !== "default") {
      setAttendanceMode("default");
    }
  }, [additionalAttendance, attendanceMode]);

  useEffect(() => {
    let initialSchoolYear = null;
    let initialSchoolYearId = null;

    if (organSettingInfo.schoolYear === null) {
      if (
        registrationInfoData &&
        settingData &&
        settingData.length > 0 &&
        schoolYearPeriodData !== undefined
      ) {
        const initialData = findInitialSchoolYear(settingData, {
          periodData: schoolYearPeriodData,
        })?.row;
        if (!initialData) return;

        initialSchoolYear = initialData.school_year;
        initialSchoolYearId = initialData.id;
        setOrganSettingInfo((organSettingInfo) => ({
          ...organSettingInfo,
          schoolYear: initialSchoolYear,
          schoolYearId: initialSchoolYearId,
        }));
        //rows 데이터처리
        if (initialSchoolYear) {
          const sortedRows = generateRegistrationRows(
            registrationInfoData,
            settingData,
            initialSchoolYear,
            organSettingInfo.organPathForSort
          );

          setRows(sortedRows);
        }
      }
    } else {
      //rows 데이터처리
      if (registrationInfoData && settingData) {
        // console.log("settingData", settingData);

        //사실 Mui dataGrid row용으로 만든 건데, 유용해서 계속 쓰자.
        const sortedRows = generateRegistrationRows(
          registrationInfoData,
          settingData,
          organSettingInfo.schoolYear,
          organSettingInfo.organPathForSort
        );
        setRows(sortedRows);
        // console.log("sortedRows2", sortedRows);
      }
    }

    // attendanceData를 학년도기준으로 불러와야 함
    // 그렇게 불러온 다음에 프론트에서 과정 학년, 반, 월별로 필터해서 보여주면 되겠군
    // 아니야 불러오는 데이터가 많고 복잡하니 다 불러와서 프론트에서 조합하자.

    // 그리고 앗싸리 서버에서 불러올 때 username을 기준으로 fullname, hierarchy_level을 가져오고 1년, 학기별 통계도 서버에서 만들어서 오자
    // 프론트에서는 해당학년도 학사설정이 안되어 있으면 관리자에게 하라고 하라고 alert 뜨게 하고.... / 이참에 alert 형식을 만들까?
    // 월별은 프론트에서 하자

    // 여기 dependency가 있어서 schoolYear와 organPathForSort 필터에 맞게 필터되어서 배포된다.
  }, [
    organSettingInfo.organPathForSort,
    organSettingInfo.schoolYear,
    organSettingInfo.schoolYearId,
    registrationInfoData,
    settingData,
    schoolYearPeriodData,
  ]);

  useEffect(() => {
    // 데이터가 존재하고, 학년 ID가 정의되어 있으며, attendanceQuery도 사용 가능한 경우에만 실행
    if (
      organSettingInfo.schoolYearId !== null &&
      schoolYearPeriodData &&
      schoolYearPeriodData.length > 0 &&
      attendanceQuery
    ) {
      let filteredPeriod = [];
      let hierarchyOnePeriod = [];
      let hierarchyTwoPeriod = [];

      filteredPeriod = schoolYearPeriodData.filter(
        (r) =>
          r.school_year_id.toString() ===
          organSettingInfo.schoolYearId.toString()
      );

      hierarchyOnePeriod = filteredPeriod.filter(
        (r) =>
          r.applied_hierarchy_one !== null &&
          r.applied_hierarchy_one === Number(organSettingInfo.hierarchyOneId)
      );

      if (hierarchyOnePeriod.length > 0) {
        filteredPeriod = hierarchyOnePeriod;
      }

      hierarchyTwoPeriod = filteredPeriod.filter(
        (r) =>
          r.applied_hierarchy_two !== null &&
          r.applied_hierarchy_two === Number(organSettingInfo.hierarchyTwoId)
      );

      if (hierarchyTwoPeriod.length > 0) {
        filteredPeriod = hierarchyTwoPeriod;
      }

      if (filteredPeriod.length > 0) {
        filteredPeriod = filteredPeriod.sort((a, b) => {
          // hierarchy_two_name이 "전체"일 경우 우선 정렬
          if (
            a.hierarchy_two_name === "전체" &&
            b.hierarchy_two_name !== "전체"
          ) {
            return -1; // a가 b보다 앞에 오도록
          } else if (
            a.hierarchy_two_name !== "전체" &&
            b.hierarchy_two_name === "전체"
          ) {
            return 1; // b가 a보다 앞에 오도록
          }

          // hierarchy_one_name이 "전체"일 경우 우선 정렬
          if (
            a.hierarchy_one_name === "전체" &&
            b.hierarchy_one_name !== "전체"
          ) {
            return -1;
          } else if (
            a.hierarchy_one_name !== "전체" &&
            b.hierarchy_one_name === "전체"
          ) {
            return 1;
          }

          return 0; // 그 외의 경우 순서 유지
        });
      }

      const calculatedThisYearDates =
        filteredPeriod.length > 0 ? filteredPeriod[0] : null;

      setThisYearDates(calculatedThisYearDates);

      // 학년도 기간 내의 년-월 목록 생성
      if (
        calculatedThisYearDates &&
        calculatedThisYearDates.start_date &&
        calculatedThisYearDates.end_date
      ) {
        const startDate = new Date(calculatedThisYearDates.start_date);
        const endDate = new Date(calculatedThisYearDates.end_date);

        const yearMonths = [];
        let currentDate = new Date(startDate);
        currentDate.setDate(1); // 월의 첫 날로 설정

        while (currentDate <= endDate) {
          const year = currentDate.getFullYear();
          const month = currentDate.getMonth() + 1;
          const yearMonth = `${year}-${String(month).padStart(2, "0")}`;
          yearMonths.push({ year, month, yearMonth });
          currentDate.setMonth(currentDate.getMonth() + 1);
        }

        setValidYearMonths(yearMonths);

        // selectedMonth가 비어있거나 유효하지 않으면 현재 날짜 기준으로 초기화
        if (yearMonths.length > 0) {
          if (!selectedMonth) {
            // selectedMonth가 비어있으면 현재 날짜 기준으로 설정
            const today = new Date();
            const currentYear = today.getFullYear();
            const currentMonth = today.getMonth() + 1;
            const currentYearMonth = `${currentYear}-${String(
              currentMonth
            ).padStart(2, "0")}`;

            // 현재 날짜가 유효한 년-월 목록에 있는지 확인
            const foundCurrentMonth = yearMonths.find(
              (ym) => ym.yearMonth === currentYearMonth
            );

            if (foundCurrentMonth) {
              // 현재 날짜가 학년도 기간 내에 있으면 현재 달 선택
              setSelectedMonth(currentYearMonth);
            } else {
              // 현재 날짜가 학년도 기간 밖이면 첫 번째 월로 초기화
              // 예: 현재가 2024년 12월인데 2022년 2월~2023년 2월 학년도를 보는 경우
              setSelectedMonth(yearMonths[0].yearMonth);
            }
          } else {
            // 이미 선택된 월이 있으면 유효성만 확인
            const isValid = yearMonths.some(
              (ym) => ym.yearMonth === selectedMonth
            );
            if (!isValid) {
              // 유효하지 않으면 현재 날짜 기준으로 재설정
              const today = new Date();
              const currentYear = today.getFullYear();
              const currentMonth = today.getMonth() + 1;
              const currentYearMonth = `${currentYear}-${String(
                currentMonth
              ).padStart(2, "0")}`;

              const foundCurrentMonth = yearMonths.find(
                (ym) => ym.yearMonth === currentYearMonth
              );

              if (foundCurrentMonth) {
                setSelectedMonth(currentYearMonth);
              } else {
                // 현재 날짜가 리스트에 없으면 첫 번째 월로 초기화
                setSelectedMonth(yearMonths[0].yearMonth);
              }
            }
          }
        }
      } else {
        setValidYearMonths([]);
      }

      // 학년 데이터가 존재하고, 정규 표현식을 통과한 경우에만 진행

      if (calculatedThisYearDates) {
        if (
          /^\d{4}-\d{2}-\d{2}$/.test(calculatedThisYearDates.start_date) &&
          /^\d{4}-\d{2}-\d{2}$/.test(calculatedThisYearDates.end_date)
        ) {
          const queryConditions = {
            schoolid: schoolId,
            startdate: calculatedThisYearDates.start_date,
            enddate: calculatedThisYearDates.end_date,
          };
          attendanceQuery.mutate(queryConditions, {
            onSuccess: (data) => {
              setAttendanceRecord(data);
            },
            onError: (error) => {
              console.error(error);
              alert(error.message);
            },
          });
        } else {
          setAlertModalState({
            visible: true,
            message: t("teacher.attendanceStatistics.invalidSchoolYearPeriod"),
          });
        }
      } else {
        setAttendanceRecord([]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    organSettingInfo.schoolYearId,
    organSettingInfo.hierarchyOneId,
    organSettingInfo.hierarchyTwoId,
    schoolId,
  ]); // refetchData 함수는 useCallback에 의해 메모이제이션되어, organSettingInfo.schoolYearId가 변경될 때만 재생성됩니다.

  //아래 dropdown에 세트로 들어가야 할 애들이다.
  const handleDropdownChange = (e) => {
    const { name, value } = e.target;
    const selectedOptionText = e.target.selectedOptions[0].text;

    const dataName = e.target.selectedOptions[0].getAttribute("data-name");
    const organPath = dataName + "/" + value;

    let lastIndex = organSettingInfo.organPathForSort.lastIndexOf("/");
    const previousPath = organSettingInfo.organPathForSort.substring(
      0,
      lastIndex
    );
    // 100%는 아니지만 적당히 작동하니까...
    // console.log("previousPath", previousPath);
    // console.log("organPath", organPath);

    if (!organPath.startsWith("nu")) {
      setOrganSettingInfo((organSettingInfo) => ({
        ...organSettingInfo,
        organPathForSort: organPath,
      }));
    } else {
      setOrganSettingInfo((organSettingInfo) => ({
        ...organSettingInfo,
        organPathForSort: previousPath,
      }));
    }

    if (name === "0") {
      setOrganSettingInfo((organSettingInfo) => ({
        ...organSettingInfo,
        schoolYear: selectedOptionText,
        schoolYearId: value,
        hierarchyOneId: "0",
      }));
      // 학년도 변경 시 selectedMonth 초기화 (다음 useEffect에서 현재 날짜 기준으로 재설정됨)
      setSelectedMonth("");
    }

    if (name === "1") {
      setOrganSettingInfo((organSettingInfo) => ({
        ...organSettingInfo,
        hierarchyOneId: value,
        hierarchyTwoId: "",
        hierarchyThreeId: "",
      }));
    }

    if (name === "2") {
      setOrganSettingInfo((organSettingInfo) => ({
        ...organSettingInfo,
        hierarchyTwoId: value,
        hierarchyThreeId: "",
      }));
    }

    if (name === "3") {
      setOrganSettingInfo((organSettingInfo) => ({
        ...organSettingInfo,
        hierarchyThreeId: value,
      }));
    }
  };

  const onChangeMonth = (e) => {
    setSelectedMonth(e.target.value);
  };

  const preNextMonth = (preNext) => {
    if (!validYearMonths || validYearMonths.length === 0) return;

    const currentIndex = validYearMonths.findIndex(
      (ym) => ym.yearMonth === selectedMonth
    );

    if (preNext === "pre") {
      if (currentIndex > 0) {
        setSelectedMonth(validYearMonths[currentIndex - 1].yearMonth);
      }
    }

    if (preNext === "next") {
      if (currentIndex < validYearMonths.length - 1) {
        setSelectedMonth(validYearMonths[currentIndex + 1].yearMonth);
      }
    }
  };

  const handleAttendanceModeChange = (mode) => {
    if (attendanceMode === mode) return;

    setAttendanceMode(mode);
  };

  const statisticsTitle =
    attendanceMode === "additional"
      ? `${additionalAttendance?.title || "추가 출석"} 통계`
      : t("teacher.attendanceStatistics.title");

  // Function to handle printing the statistics table using react-to-print
  const triggerPrint = useReactToPrint({
    content: () => printAreaRef.current,
    documentTitle: `${organSettingInfo.schoolYear}-${selectedMonth}-${statisticsTitle}`,
    onBeforePrint: () => {
      // 인쇄 전 스크롤 제거 및 스타일 조정
      const overflowElements = printAreaRef.current?.querySelectorAll(
        ".overflow-auto, .print-no-overflow"
      );
      overflowElements?.forEach((el) => {
        el.style.overflow = "visible";
        // el.style.height = "auto";
        // el.style.maxHeight = "none";
        // el.style.width = "100%";
      });

      // monthTitle은 CSS로 처리 (JavaScript에서는 제거)
      // const monthTitles = printAreaRef.current?.querySelectorAll(".monthTitle");
      // monthTitles?.forEach((el) => {
      //   el.style.display = "block";
      // });

      // 테이블 스타일 조정
      const tables = printAreaRef.current?.querySelectorAll("table");
      tables?.forEach((table) => {
        table.style.width = "100%";
        table.style.tableLayout = "fixed";
      });

      // 고정 컬럼의 width를 명시적으로 설정 (max, width, min 모두 동일하게)
      const fixedCells =
        printAreaRef.current?.querySelectorAll(".fixed-column");
      fixedCells?.forEach((cell) => {
        // 인라인 스타일에서 width를 가져오거나 기본값 사용
        const currentWidth = cell.style.width || getComputedStyle(cell).width;
        if (currentWidth && currentWidth !== "auto") {
          cell.style.width = currentWidth;
          cell.style.minWidth = currentWidth;
          cell.style.maxWidth = currentWidth;
        }
      });

      // 날짜 컬럼은 고정 너비로 설정
      const dayCells = printAreaRef.current?.querySelectorAll(".day-column");
      dayCells?.forEach((cell) => {
        cell.style.minWidth = "25px";
        cell.style.width = "25px";
        cell.style.maxWidth = "25px";
      });
    },
    onAfterPrint: () => {
      // 인쇄 후 원래대로 복구
      const overflowElements = printAreaRef.current?.querySelectorAll(
        ".overflow-auto, .print-no-overflow"
      );
      overflowElements?.forEach((el) => {
        el.style.overflow = "";
        // el.style.height = "";
        // el.style.maxHeight = "";
        // el.style.width = "";
      });

      // monthTitle은 CSS로 처리
      // const monthTitles = printAreaRef.current?.querySelectorAll(".monthTitle");
      // monthTitles?.forEach((el) => {
      //   el.style.display = "none";
      // });

      const tables = printAreaRef.current?.querySelectorAll("table");
      tables?.forEach((table) => {
        table.style.width = "";
        table.style.tableLayout = "";
      });

      // const fixedCells =
      //   printAreaRef.current?.querySelectorAll(".fixed-column");
      // fixedCells?.forEach((cell) => {
      //   cell.style.width = "";
      //   cell.style.minWidth = "";
      //   cell.style.maxWidth = "";
      // });

      // const dayCells = printAreaRef.current?.querySelectorAll(".day-column");
      // dayCells?.forEach((cell) => {
      //   cell.style.minWidth = "";
      //   cell.style.width = "";
      //   cell.style.maxWidth = "";
      // });
    },
    removeAfterPrint: true,
    pageStyle: `
      @page {
        size: landscape;
        margin: 10mm;
      }

      @media print {
        * {
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }

        body {
          font-family: Arial, sans-serif;
          margin: 0 !important;
          padding: 0 !important;
        }

        /* 스크롤 완전 제거 - 가장 중요! */
        .overflow-auto,
        .print-no-overflow {
          overflow: visible !important;
          height: auto !important;
          max-height: none !important;
          width: 100% !important;
          display: block !important;
        }

        .page-break {
          page-break-after: always;
        }

        /* 인쇄 시 monthTitle 표시 */
        .monthTitle {
          display: block !important;
          font-size: 14px;
          font-weight: bold;
          margin-bottom: 10px;
        }

        .secondTitle {
          margin-bottom: 5px;
          font-size: 12px;
          font-weight: bold;
        }

        /* 테이블 스타일 - 페이지에 맞춤 */
        .tableStyle {
          font-size: 11px !important;
          width: 100% !important;
          border-collapse: collapse !important;
          table-layout: fixed !important;
        }

        .tableStyle th {
          padding: 0px !important;
          text-align: center !important;
          background-color: #f0f0f0 !important;
          border: 1px solid black !important;
          word-wrap: break-word !important;
        }

        .tableStyle td {
          padding: 0px !important;
          text-align: center !important;
          border: 1px solid black !important;
          word-wrap: break-word !important;
        }

        /* 고정 컬럼은 설정된 width 유지 (늘어나지 않도록 max-width도 설정) */
        thead .fixed-column,
        tbody .fixed-column {
          white-space: nowrap !important;
          overflow: hidden !important;
        }

        /* 날짜 컬럼은 최소 너비로 축소 */
        .day-column {
          min-width: 18px !important;
          width: 18px !important;
          max-width: 18px !important;
          white-space: nowrap !important;
        }

        th, td {
          border: 1px solid black !important;
          padding: 0px !important;
        }

        /* 화면 요소 숨김 */
        .no-print {
          display: none !important;
        }

        /* Bootstrap 컨테이너 여백 제거 */
        .container,
        .container-fluid {
          padding: 0 !important;
          margin: 0 !important;
        }

        /* 인쇄할 영역 최대화 */
        #printArea {
          width: 100% !important;
          margin: 0 !important;
          padding: 0 !important;
        }

        .page {
          margin: 0 !important;
          padding: 3px !important;
        }

        /* 테이블이 페이지를 넘지 않도록 */
        table {
          max-width: 100% !important;
          page-break-inside: auto !important;
        }

        tr {
          page-break-inside: avoid !important;
          page-break-after: auto !important;
        }
      }
    `,
  });

  const handlePrint = (schoolYear, selectedMonth) => {
    triggerPrint();
  };

  // Function to handle exporting the statistics to Excel
  // const handleExport = () => {
  //   // exportToExcel('attendance-table');
  // };

  if (isLoading) {
    return <LoadingInside />;
  }

  if (!hasPermission(null, "can_access")) {
    return (
      <SimpleAlertMessageBlock
        message={t("common.messages.noAccessPermission")}
      />
    );
  }

  // Excel 다운로드 함수 구현
  const handleExport = () => {
    const tableEl = document.querySelector(".tableStyle");
    if (!tableEl) return;

    // 새 워크북 생성
    const wb = XLSX.utils.book_new();

    // 모든 테이블을 순회하며 데이터 추출
    const tables = document.querySelectorAll("#printArea .tableStyle");

    tables.forEach((table, index) => {
      // 테이블을 워크시트로 변환
      const ws = XLSX.utils.table_to_sheet(table);

      // 워크시트 스타일 조정
      const range = XLSX.utils.decode_range(ws["!ref"]);
      for (let R = range.s.r; R <= range.e.r; ++R) {
        for (let C = range.s.c; C <= range.e.c; ++C) {
          const cell = ws[XLSX.utils.encode_cell({ r: R, c: C })];
          if (!cell) continue;

          // 셀 스타일 설정
          cell.s = {
            alignment: { horizontal: "center", vertical: "center" },
            border: {
              top: { style: "thin" },
              bottom: { style: "thin" },
              left: { style: "thin" },
              right: { style: "thin" },
            },
          };
        }
      }

      // 워크북에 워크시트 추가
      let sheetName = `attendance${index}`; // 기본 시트 이름 설정
      const secondTitleElements = document.querySelectorAll(".secondTitle");

      if (secondTitleElements.length > index) {
        const classInfo = secondTitleElements[index].textContent.trim();
        if (classInfo) {
          // 공백을 언더스코어로 대체하고 31자로 제한
          sheetName = classInfo.replace(/\s+/g, "_").substring(0, 31);
        }
      }

      XLSX.utils.book_append_sheet(wb, ws, sheetName);
    });

    // 파일 다운로드
    const fileName = `${organSettingInfo.schoolYear}-${selectedMonth}-${statisticsTitle}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  return (
    <>
      <div className="ms-3 basicWidth">
        <div className="container-fluid">
          <div className="row mb-4 mt-2">
            <div className="col-3">
              <div className="d-flex">
                <h3
                  className={`fw-bold mt-3 ${
                    attendanceMode === "additional" ? "text-info" : ""
                  }`}
                >
                  {statisticsTitle}
                </h3>
              </div>
            </div>
            <div className="col-9 d-flex gap-2 align-items-center">
              {additionalAttendance && (
                <div className="btn-group flex-shrink-0" role="group">
                  <button
                    type="button"
                    className={`btn btn-sm ${
                      attendanceMode === "default"
                        ? "btn-primary"
                        : "btn-outline-primary"
                    }`}
                    onClick={() => handleAttendanceModeChange("default")}
                  >
                    {t("teacher.attendanceStatistics.title")}
                  </button>
                  <button
                    type="button"
                    className={`btn btn-sm ${
                      attendanceMode === "additional"
                        ? "btn-info"
                        : "btn-outline-info"
                    }`}
                    onClick={() => handleAttendanceModeChange("additional")}
                  >
                    {additionalAttendance.title || "추가 출석"}
                  </button>
                </div>
              )}
              <DropdownsForAttendanceStatistics
                settingData={settingData}
                handleDropdownChange={handleDropdownChange}
                organSettingInfo={organSettingInfo}
                selectedMonth={selectedMonth}
                onChangeMonth={onChangeMonth}
                validYearMonths={validYearMonths}
              />
            </div>
          </div>
          <div className="row mb-3">
            <div className="col-7">
              <ChangeMonth preNextMonth={preNextMonth} />
            </div>
            <div className="col-5 d-flex justify-content-end">
              <button
                className="btn btn-info me-3"
                onClick={() =>
                  handlePrint(organSettingInfo.schoolYear, selectedMonth)
                }
              >
                {t("common.labels.print")}
              </button>
              <button className="btn btn-success" onClick={handleExport}>
                To Excel
              </button>
            </div>
          </div>
          <div id="printArea" ref={printAreaRef}>
            {organSettingInfo.hierarchyOneId &&
              organSettingInfo.hierarchyOneId !== "0" &&
              // key 3의 유니크한 값들을 추출
              [...new Set(rows.map((row) => row.organization_path))].map(
                (uniqueKey) => {
                  // 각 유니크한 key 값에 대해 rows를 필터링
                  // console.log("rows", rows);
                  // console.log("uniqueKey", uniqueKey);

                  const filteredRows = rows.filter(
                    (row) => row.organization_path === uniqueKey
                  );
                  // console.log("filteredRows", filteredRows);
                  return (
                    <div key={uniqueKey} className="page">
                      <h4 className="monthTitle" style={{ display: "none" }}>
                        {selectedMonth &&
                          selectedMonth
                            .replace("-", "년 ")
                            .replace(/^(\d{4})/, (m) => m.slice(2))}
                        월 {statisticsTitle}
                      </h4>
                      <div className="secondTitle">{`${filteredRows[0][1]} ${t(
                        "common.labels.schoolStage"
                      )} ${filteredRows[0][2]} ${t(
                        "common.labels.studentYear"
                      )} ${filteredRows[0][3]} ${t(
                        "common.labels.class"
                      )}`}</div>
                      <AttendanceStatisticsTable
                        rows={filteredRows}
                        attendanceRecord={attendanceRecord}
                        organSettingInfo={organSettingInfo}
                        selectedMonth={selectedMonth}
                        conditions={conditions}
                        attendanceMode={attendanceMode}
                        additionalAttendance={additionalAttendance}
                      />
                      <div className="page-break"></div> {/* 페이지 분리 */}
                    </div>
                  );
                }
              )}
          </div>
        </div>
      </div>
    </>
  );
}
